import * as userGroupService from "source/models/services/userGroupService";
import { Translations } from "ui/Scripts/source/models/translations";
import { ISessionState } from "source/models/session/sessionState";
import { Dialog } from "source/components/framework/Dialog/Dialog";
import { ICompanyMenuItem } from "ui/Scripts/source/models/company/companyMenuItem";
import { IUserGroupListItem } from "ui/Scripts/source/models/userGroup/userGroupListItem";
import { Alert, Box, Typography } from "@mui/material";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";
import { ActionContainer } from "source/components/framework/ActionContainer";
import { ListSearchFilter } from "source/components/framework/List/ListSearchFilter";
import { UserGroupListItems } from "ui/Scripts/source/models/userGroup/userGroupListItems";
import { AppUtils } from "source/framework/appUtils";
import { TCodeCompanyCode } from "ui/Scripts/source/models/common/codeCompanyCode";
import { List } from "source/components/framework/List/List";
import { IListValueLineData } from "source/components/framework/List/ListLine";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import React, { useEffect, useRef, useState } from "react";

interface ISelectUserGroupDialogWithCompanySelectProps {
    sessionState: ISessionState;
    companies: ICompanyMenuItem[];
    multiSelect?: boolean;
    ignoreUserGroups?: TCodeCompanyCode[]
    requireUserGroups?: TCodeCompanyCode[]
    ignoreQrCodeUserGroups?: boolean;
    requireQrCodeUserGroups?: boolean
    onGetUserGroupListItems?: (companyIds: string[], pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean,
        ignoreUserGroups: TCodeCompanyCode[], requireUserGroups: TCodeCompanyCode[], ignoreQrCodeUserGroups: boolean, requireQrCodeUserGroups: boolean) => Promise<UserGroupListItems>;
    onOk: (companyGroups: IUserGroupListItem[]) => void;
    onCancel: () => void;
}

interface ISelectUserGroupDialogWithCompanySelectState {
    intialized: boolean;
    isLoading: boolean;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    items: IUserGroupListItem[];
    selectedId: string;
    checkedIds: string[];
    selectedCompanies: ICompanyMenuItem[];
}

export const SelectUserGroupDialogWithCompanySelect = (props: ISelectUserGroupDialogWithCompanySelectProps) => {
    const _isMounted = useRef(false);
    const [state, setState] = useState<ISelectUserGroupDialogWithCompanySelectState>({
        intialized: false,
        isLoading: false,
        selectedId: null,
        checkedIds: [],
        filter: "",
        sortColumn: "name",
        sortOrderIsAsc: true,
        items: [],
        selectedCompanies: []
    });

    const searchItems = async (filter: string, sortColumn: string, sortOrderIsAsc: boolean) => {
        if (!_isMounted.current) return;
        setState(old => ({ ...old, isLoading: true }));
        const getUserGroupListItems = props.onGetUserGroupListItems ? props.onGetUserGroupListItems : userGroupService.getUserGroupListItemsForUser;
        const result = await AppUtils.callService(() => getUserGroupListItems(props.companies.map(i => i.id), 10000, 1, filter, sortColumn, sortOrderIsAsc,
            props.ignoreUserGroups, props.requireUserGroups, props.ignoreQrCodeUserGroups, props.requireQrCodeUserGroups), false, true, () => { if (!_isMounted.current) return; setState(old => ({ ...old, isLoading: false })); });
        if (!result) return;
        if (!_isMounted.current) return;
        setState(old => ({
            ...old,
            ...(() => {
                const items = List.getListItems(old.items, old.selectedId, old.checkedIds, result.items, true, false);
                const ids = List.getSelectedIds(items.selectedId, items.checkedIds);
                return {
                    items: items.items,
                    selectedId: items.selectedId,
                    checkedIds: items.checkedIds,
                    selectedCompanies: props.companies.filter(i => items.items.some(ug => ids.indexOf(ug.id) > -1 && ug.companyCode === i.code))
                };
            })(),
            intialized: true,
            filter,
            sortColumn,
            sortOrderIsAsc,
        }));
    };

    useEffect(() => {
        _isMounted.current = true;

        return () => {
            _isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        searchItems(state.filter, state.sortColumn, state.sortOrderIsAsc);
    }, [ //TODO: Caller component should change references only when values actually change, then this useEffect could use also reference equality checks instead of mapping to ids and strings
        (props.companies ? props.companies.map(i => i.id) : []).join(","),
        (props.ignoreUserGroups ? props.ignoreUserGroups.map(i => i.companyCode + "#" + i.code) : []).join(","),
        (props.requireUserGroups ? props.requireUserGroups.map(i => i.companyCode + "#" + i.code) : []).join(","),
        props.ignoreQrCodeUserGroups,
        props.requireQrCodeUserGroups,
        props.onGetUserGroupListItems
    ]);

    const changeFilter = (filter: string) => {
        searchItems(filter, state.sortColumn, state.sortOrderIsAsc);
    };

    const handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, _nextPage: boolean) => {
        searchItems(state.filter, sortColumn, sortOrderIsAsc);
    };

    const handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        setState(old => ({
            ...old,
            ...(() => {
                const ids = List.getSelectedIds(selectedId, checkedIds);
                return {
                    selectedCompanies: props.companies.filter(i => old.items.some(ug => ids.indexOf(ug.id) > -1 && ug.companyCode === i.code))
                };
            })(),
            selectedId,
            checkedIds,
        }));
    };

    const handleOk = () => {
        const ids = List.getSelectedIds(state.selectedId, state.checkedIds);
        if (ids.length < 1) return;
        props.onOk(state.items.filter(i => ids.indexOf(i.id) > -1));
    };

    const selectedCompanies = state.selectedCompanies.filter(i => i.id !== props.sessionState.companyId);

    return (
        <Dialog
            id="selectUserGroupDialogWithCompanySelect"
            maxWidth="md"
            maxHeight="726px"
            hide={!state.intialized}
            title={Translations.SelectUserGroup}
            body={
                <VerticalContainer
                    before={
                        <ActionContainer
                            leftItems={
                                <ListSearchFilter
                                    searchFilter={state.filter}
                                    onSearchClick={changeFilter}
                                />
                            }
                        />
                    }
                    main={<>
                        <List
                            multiSelect={props.multiSelect}
                            intialized={state.intialized}
                            isLoading={state.isLoading}
                            hasMore={false}
                            height="100%"
                            header={{
                                columns: [
                                    { size: 6, column: "name", content: Translations.Name },
                                    { size: 6, column: "comment", content: Translations.Description },
                                ]
                            }}
                            lines={(() => {
                                const multipleCompaniesInUse = props.companies.length > 1;
                                const lines: IListValueLineData[] = [];
                                for (const company of props.companies) {
                                    const companyUserGroups = state.items.filter(i => i.companyCode === company.code);
                                    if (companyUserGroups.length < 1) continue;
                                    //Company row
                                    if (multipleCompaniesInUse) {
                                        lines.push({
                                            columns: [
                                                {
                                                    size: 12,
                                                    content:
                                                        <Box display="flex" alignItems="center">
                                                            <AvatarImage
                                                                name={company.name}
                                                                image={company.logo}
                                                                tooltip={company.name}
                                                                marginRight=".5rem"
                                                            />
                                                            <Typography>{company.name}</Typography>
                                                        </Box>
                                                }
                                            ],
                                            id: company.id,
                                            unselectable: true,
                                        });
                                    }
                                    //UserGroup rows
                                    for (const userGroup of companyUserGroups) {
                                        lines.push({
                                            columns: [
                                                { size: 6, content: userGroup.name },
                                                { size: 6, content: userGroup.comment }
                                            ],
                                            id: userGroup.id
                                        });
                                    }
                                }
                                return lines;
                            })()}
                            sortColumn={state.sortColumn}
                            sortOrderIsAsc={state.sortOrderIsAsc}
                            selectedId={state.selectedId}
                            checkedIds={state.checkedIds}
                            onSetSelectedIds={handleSetSelectedIds}
                            onSearchLines={handleSearchLines}
                        />
                    </>}
                    after={
                        <>
                            {selectedCompanies.length > 0 &&
                                <Alert severity="info" sx={{ marginTop: ".5rem", whiteSpace: "pre-line" }}>
                                    {String.format(Translations.UserRecordWillBeSharedWithCompanyParameter, selectedCompanies.length > 10
                                        ? [...selectedCompanies.slice(0, 10).map(i => i.name), "..."].join(", ")
                                        : selectedCompanies.slice(0, 10).map(i => i.name).join(", "))}
                                </Alert>
                            }
                        </>
                    }
                />
            }
            buttons={[
                { title: Translations.Cancel, variant: "outlined", onClick: props.onCancel },
                { title: Translations.OK, variant: "confirmation", onClick: handleOk },
            ]}
            onClose={props.onCancel}
        />
    );
};